/* eslint-disable class-methods-use-this */

// Third-party
import React, { Component, CSSProperties } from 'react';
import debounce from 'lodash/debounce';
import isEqual from 'lodash/isEqual';
import classNames from 'classnames';

// Namespaces
import * as ProposalNS from '../../Proposal';
import * as ProposalElementsNS from '../../ProposalElements';
import { ProposalTemplateOptions } from '@/redux/modules/types/proposal-v2';

// Utils
import colorUtils from '@/utils/colors';
import domUtils from '@/utils/dom';
import { formatMessage } from '@/utils/i18n';
import { PROPOSAL_ORIENTATION, PROPOSAL_BANNER_STYLES } from '../../utils/constants';

// Components
import ProposalPageComponent from './ProposalPageComponent';
import ProposalSectionComponent from './ProposalSectionComponent';
import { GenericEmptyState } from '@/views/components/misc';

// Themes
import '../../themes/index';

import Dictionary from '@/types/dictionary';
import ProposalToolbarOption from '@/types/proposal-toolbar-option';
import ProposalTextAreaData from '@/types/proposal-text-area-data';
import User from '@/types/user';
import { LoadingSpinner } from '@/views/components/common';
import { Pill } from '@capintel/component-lib/pill';

// Constants
const DARK_TEXT_COLOR = '#000000';
const LIGHT_TEXT_COLOR = '#ffffff';
const LIGHT_PATTERN_COLOR = 'rgba(255,255,255,0.07)';
const DARK_PATTERN_COLOR = 'rgba(0,0,0,0.07)';

type Props = {
  user: User;
  proposal: ProposalNS.Proposal;
  selectedDivisions: string[];
  selectedSections: string[];
  selectedViews: Dictionary<string>;
  setToolbarOption: (key: string, toolbarOption: ProposalToolbarOption) => void;
  proposalTemplateOptions: ProposalTemplateOptions;
  setTextAreaData: (key: string, textAreaData: ProposalTextAreaData) => void;
  setSelectedView: (key: string, value: string) => void;
  pages: ProposalElementsNS.ProposalSectionElement[][];
  pageRefs: Array<React.RefObject<HTMLDivElement>>;
  styleSheetLoaded: boolean;
};

type State = {
  pageWidth: number;
  pageHeight: number;
  pageScale: number;
};

// TODO: For better PDF generation, possibly revise these numbers as they don't align with standard 96 DPI standard for web. These values suggest a different scaling
// These values are aligned with the viewport used for PDFs - do not change.
const PORTRAIT_PAGE_WIDTH = 1025;
const LANDSCAPE_PAGE_WIDTH = 1326;

const LANDSCAPE_PAGE_RATIO = 0.7727; // 8.5/11
const PORTRAIT_PAGE_RATIO = 1.2941; // 11/8.5

// Configuration: List of section keys that should have multi-page disclaimer logic applied
const SECTIONS_WITH_MULTI_PAGE_DISCLAIMER_LOGIC = ['PORTFOLIO_HOLDINGS_SUMMARY', 'TOP_25_HOLDINGS', 'TOP_50_HOLDINGS'];

class ProposalPreviewComponent extends Component<Props, State> {
  readonly state: State = {
    pageWidth: 1, // Will be properly initialized inside componentDidMount - use 1 to prevent division by zero
    pageHeight: 1,
    pageScale: 1,
  };

  handleResize = debounce(() => {
    this.adjustProposalPreviewSize();
  }, 50);

  componentDidMount() {
    window.addEventListener('resize', this.handleResize);
  }

  shouldComponentUpdate(nextProps: Props, nextState: State) {
    return !isEqual(nextProps, this.props) || !isEqual(nextState, this.state);
  }

  componentDidUpdate(prevProps: Props) {
    const {
      proposal: { items, template },
      selectedSections,
      selectedDivisions,
    } = this.props;

    const templateChanged = template.key !== prevProps.proposal.template.key;
    const itemsChanged = items.length !== prevProps.proposal.items.length;
    const sectionChanged = selectedSections.length !== prevProps.selectedDivisions.length || selectedDivisions.length !== prevProps.selectedDivisions.length;

    if (templateChanged || itemsChanged || sectionChanged) {
      domUtils.fireResizeEvent();
    }
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.handleResize);
  }

  adjustProposalPreviewSize = () => {
    const { pageWidth: prevWidth, pageHeight: prevHeight, pageScale: prevScale } = this.state;

    const container = document.getElementById('proposal-preview-component');

    if (!container) {
      return;
    }

    const {
      proposal: { template },
    } = this.props;

    const landscapeOrientation = this.props.proposalTemplateOptions.styleOptions.orientation === PROPOSAL_ORIENTATION.LANDSCAPE;

    let newWidth = landscapeOrientation ? LANDSCAPE_PAGE_WIDTH : PORTRAIT_PAGE_WIDTH;
    const pageRatio = landscapeOrientation ? LANDSCAPE_PAGE_RATIO : PORTRAIT_PAGE_RATIO;

    if (template.pageFormat !== undefined) {
      newWidth = template.pageFormat.width;
    }

    let newHeight = Math.floor(newWidth * pageRatio);

    if (template.pageFormat !== undefined) {
      newHeight = template.pageFormat.height;
    }

    const newScale = Math.min(container.clientWidth / newWidth, 1);

    if (newWidth !== prevWidth || newHeight !== prevHeight || newScale !== prevScale) {
      this.setState(
        {
          pageHeight: newHeight,
          pageWidth: newWidth,
          pageScale: newScale,
        },
        () => {
          domUtils.fireResizeEvent();
        },
      );
    }
  };

  getStyleOptions = (): ProposalNS.ProposalStyleOptionsV2 => {
    const {
      proposalTemplateOptions: {
        styleOptions,
        styleOptions: { banners },
      },
    } = this.props;

    const background = banners.background || PROPOSAL_BANNER_STYLES[0].background;
    const isLight = colorUtils.isLight(background);

    return {
      ...styleOptions,
      banners: {
        ...styleOptions.banners,
        background,
        color: isLight ? DARK_TEXT_COLOR : LIGHT_TEXT_COLOR,
        patternColor: banners.patternColor || (isLight ? LIGHT_PATTERN_COLOR : DARK_PATTERN_COLOR),
      },
    };
  };

  getProposalPageStyles = (pageIndex: number) => {
    const { pageWidth, pageHeight, pageScale } = this.state;
    const marginTop = pageIndex === 0 ? '0' : `-${pageHeight - pageHeight * pageScale}px`;

    return {
      pageStyles: {
        width: `${pageWidth}px`,
        minHeight: `${pageHeight}px`,
        height: `${pageHeight}px`,
        transform: `scale(${pageScale})`,
        marginTop,
      } as CSSProperties,
      iconStyles: {
        top: pageIndex === 0 ? 0 : marginTop,
      } as CSSProperties,
    };
  };

  renderSection(section: ProposalElementsNS.ProposalSectionElement, pageNumber: number, numberOfPages: number) {
    const { proposal } = this.props;

    return (
      <ProposalSectionComponent
        key={`section ${(section as ProposalElementsNS.ProposalSectionElement).key}`}
        proposal={proposal}
        section={section as ProposalElementsNS.ProposalSectionElement}
        selectedViews={this.props.selectedViews}
        hiddenLayouts={this.props.proposalTemplateOptions.hiddenLayouts}
        pageNumber={pageNumber}
        numberOfPages={numberOfPages}
        setToolbarOption={this.props.setToolbarOption}
        setTextAreaData={this.props.setTextAreaData}
        setSelectedView={this.props.setSelectedView}
      />
    );
  }

  renderEmptyState() {
    const { proposal } = this.props;
    return (
      <GenericEmptyState
        header={formatMessage('Proposal.EmptyTitle')}
        body={formatMessage(!proposal.items.length ? 'Proposal.EmptyItems' : 'Proposal.EmptyContent')}
      />
    );
  }

  handlePageDisclaimers() {
    const { pages } = this.props;

    // Group pages by base section key and handle multi-page sections
    // Each time we see _0, it potentially starts a new section instance
    const sectionGroups: { [baseKey: string]: { pageIndex: number; element: ProposalElementsNS.ProposalSectionElement; suffix: string }[] } = {};

    // First pass: collect all elements and group by base section key
    pages.forEach((proposalElementArray, pageIndex) => {
      proposalElementArray.forEach(element => {
        if (element.key && element.disclaimerConfigs) {
          // Extract base key (remove suffix like _0, _1, etc.)
          const baseKey = element.key.replace(/_\d+$/, '');

          // Only apply logic to sections in the configuration list
          if (SECTIONS_WITH_MULTI_PAGE_DISCLAIMER_LOGIC.includes(baseKey)) {
            const suffix = element.key.match(/_(\d+)$/)?.[1] || '0';

            if (!sectionGroups[baseKey]) {
              sectionGroups[baseKey] = [];
            }

            sectionGroups[baseKey].push({ pageIndex, element, suffix });
          }
        }
      });
    });

    // Second pass: for each base section key, group into separate section instances
    Object.entries(sectionGroups).forEach(([baseKey, items]) => {
      // Sort by page index
      items.sort((a, b) => a.pageIndex - b.pageIndex);

      const sectionInstances: { pageIndex: number; element: ProposalElementsNS.ProposalSectionElement; suffix: string }[][] = [];
      let currentInstance: { pageIndex: number; element: ProposalElementsNS.ProposalSectionElement; suffix: string }[] = [];

      items.forEach((item, index) => {
        if (index === 0) {
          // First item always starts a new instance
          currentInstance = [item];
        } else {
          const prevItem = items[index - 1];
          const isConsecutivePage = item.pageIndex === prevItem.pageIndex + 1;
          const isNewInstance = item.suffix === '0'; // Each _0 suffix starts a new instance

          // Start new instance if: not consecutive pages OR current suffix is _0
          if (!isConsecutivePage || isNewInstance) {
            sectionInstances.push(currentInstance);
            currentInstance = [item];
          } else {
            currentInstance.push(item);
          }
        }
      });

      // Add the last instance
      if (currentInstance.length > 0) {
        sectionInstances.push(currentInstance);
      }

      // Apply disclaimer logic for each instance
      sectionInstances.forEach((instance, instanceIndex) => {
        if (instance.length > 1) {
          const lastPageIndex = instance[instance.length - 1].pageIndex;

          instance.forEach(({ pageIndex, element }) => {
            if (pageIndex !== lastPageIndex && element.disclaimerConfigs) {
              element.disclaimerConfigs = [];
            }
          });
        }
      });
    });
  }

  renderPages() {
    const { proposal, proposalTemplateOptions, pages, pageRefs } = this.props;

    this.handlePageDisclaimers();

    // Flags to distinguish the first headers/footers shown (ie: for different styling)
    let firstHeader = true;
    let firstFooter = true;

    return pages.map((proposalElementArray, pageIndex) => {
      const { pageStyles } = this.getProposalPageStyles(pageIndex);
      const pageKey = `${proposal.template.key}-${pageIndex}`;

      const pageSections = proposalElementArray.map(element => {
        const section = this.renderSection(element, pageIndex + 1, pages.length);

        if (section === undefined) {
          return null;
        }

        return React.cloneElement(section, { key: `${section.key}-${pageKey}` });
      });

      const hideHeader: boolean = proposalElementArray.some(element => element.showHeader === false);
      const hideFooter: boolean = proposalElementArray.some(element => element.showFooter === false);

      const pageClasses = classNames({
        'first-header': !hideHeader && firstHeader,
        'first-footer': !hideFooter && firstFooter,
      });

      firstHeader = !hideHeader ? false : firstHeader;
      firstFooter = !hideFooter ? false : firstFooter;

      pageRefs[pageIndex] = React.createRef();

      return (
        <React.Fragment key={pageKey}>
          <div className="proposal-page-number">
            <Pill variant="default" size="small">
              Page {pageIndex + 1}
            </Pill>
          </div>

          <ProposalPageComponent
            className={pageClasses}
            pageStyles={pageStyles}
            proposal={proposal}
            renderedSections={pageSections}
            pageIndex={pageIndex + 1}
            numberOfPages={pages.length}
            setToolbarOption={this.props.setToolbarOption}
            proposalTemplateOptions={proposalTemplateOptions}
            hideHeader={hideHeader}
            hideFooter={hideFooter}
            ref={pageRefs[pageIndex]}
          />
        </React.Fragment>
      );
    });
  }

  renderLoadingState() {
    return (
      <div className="proposal-preview-loading-container">
        <LoadingSpinner />
      </div>
    );
  }

  render() {
    const { proposal, proposalTemplateOptions, pages, styleSheetLoaded, user } = this.props;

    const { locale, theme } = proposal.options;

    this.adjustProposalPreviewSize();

    proposalTemplateOptions.styleOptions = this.getStyleOptions();

    const gifProposal = proposal.gifItems !== undefined && proposal.gifItems.length > 0;
    const isEmpty = pages.length === 0 || (proposal.items.length === 0 && !gifProposal);
    const primaryColor = proposalTemplateOptions.styleOptions.banners.coBrandingColors?.primary || user.coBranding?.colors?.primary;

    return (
      <div
        id="proposal-preview-component"
        className={`proposal-preview-component ${theme} ${proposal.template.key}`}
        data-locale={locale}
        style={{ '--primary-selected-color': primaryColor } as React.CSSProperties}
      >
        {styleSheetLoaded ? (isEmpty ? this.renderEmptyState() : this.renderPages()) : this.renderLoadingState()}
      </div>
    );
  }
}

export default ProposalPreviewComponent;
